import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useEntityType, useEntities } from "../../../lib/hooks/use-kuvio";
import { displayFieldFor } from "../../../lib/query-keys";
import type { EntityRecord, ResolvedRef } from "../../../lib/types";

/**
 * Sprint 12 — fuzzy-search dropdown over entities of a given type. Backs the
 * single/multi reference field editors (add an FK target). Anchored inline below
 * its trigger; the parent controls mount/unmount. Caps the list for large tables.
 */

interface EntityPickerProps {
  targetTypeSlug: string;
  onPick: (ref: ResolvedRef) => void;
  onClose: () => void;
  /** Hide records already chosen (multi-ref). */
  excludeIds?: string[];
}

const MAX_RESULTS = 50;

export function EntityPicker({ targetTypeSlug, onPick, onClose, excludeIds = [] }: EntityPickerProps) {
  const type = useEntityType(targetTypeSlug);
  const entitiesQuery = useEntities(type.data);
  const [query, setQuery] = useState("");
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    function onDocClick(e: globalThis.MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const displayField = type.data ? displayFieldFor(type.data) : "name";
  const excluded = useMemo(() => new Set(excludeIds), [excludeIds]);

  const results = useMemo(() => {
    const rows = (entitiesQuery.data?.entities ?? []) as EntityRecord[];
    const q = query.trim().toLowerCase();
    return rows
      .filter((r) => !excluded.has(r.id))
      .map((r) => ({ id: r.id, name: String(r[displayField] ?? r.id) }))
      .filter((r) => !q || `${r.name} ${r.id}`.toLowerCase().includes(q))
      .slice(0, MAX_RESULTS);
  }, [entitiesQuery.data, query, displayField, excluded]);

  const box: CSSProperties = {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    zIndex: 60,
    minWidth: 240,
    maxWidth: 360,
    background: "var(--color-background-primary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-md)",
    boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
    padding: 6,
    fontFamily: "var(--font-sans)",
  };

  return (
    <div ref={boxRef} style={box} role="dialog" aria-label={`Pick ${type.data?.name ?? "record"}`}>
      <input
        ref={inputRef}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={`Search ${type.data?.plural ?? "records"}…`}
        style={{
          width: "100%",
          padding: "6px 8px",
          fontSize: 13,
          fontFamily: "var(--font-sans)",
          border: "0.5px solid var(--color-border-secondary)",
          borderRadius: "var(--border-radius-sm)",
          outline: "none",
          color: "var(--color-text-primary)",
          background: "var(--color-background-primary)",
          marginBottom: 6,
        }}
      />
      <div style={{ maxHeight: 240, overflowY: "auto" }}>
        {entitiesQuery.isLoading ? (
          <div style={emptyTextStyle}>Loading…</div>
        ) : results.length === 0 ? (
          <div style={emptyTextStyle}>{query ? "No matches." : "No records yet."}</div>
        ) : (
          results.map((r) => (
            <button
              key={r.id}
              onClick={() => onPick({ id: r.id, name: r.name, type_slug: targetTypeSlug })}
              className="kuvio-panel-row"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "6px 8px",
                border: "none",
                background: "transparent",
                textAlign: "left",
                borderRadius: "var(--border-radius-sm)",
                cursor: "pointer",
                fontFamily: "var(--font-sans)",
              }}
            >
              <span style={{ fontSize: 13, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.name}
              </span>
              <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--color-text-tertiary)", fontFamily: "var(--font-mono)", flexShrink: 0 }}>
                {r.id}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

const emptyTextStyle: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
  padding: "8px",
  textAlign: "center",
};
